import React, { useEffect, useState, ReactElement } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase/config';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Public Routes
import HomePage from './pages/public/HomePage';
import ProjectsPage from './pages/public/ProjectsPage';
import ProjectDetailPage from './pages/public/ProjectDetailPage';
import ResumePage from './pages/public/ResumePage';
import ContactPage from './pages/public/ContactPage';
import PublicationsPage from './pages/public/PublicationsPage';
import PubPackagesPage from './pages/public/PubPackagesPage';
import StarterTemplatesPage from './pages/public/StarterTemplatesPage';
import OpenSourcePage from './pages/public/OpenSourcePage';
import WritingPage from './pages/public/WritingPage';
import CoursePage from './pages/public/CoursePage';
import CourseLessonPage from './pages/public/CourseLessonPage';
import ArticleDetailPage from './pages/public/ArticleDetailPage';

// Admin Routes
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import ProfileEditor from './pages/admin/ProfileEditor';
import ProjectsEditor from './pages/admin/ProjectsEditor';
import CategoriesEditor from './pages/admin/CategoriesEditor';
import ExperienceEditor from './pages/admin/ExperienceEditor';
import EducationEditor from './pages/admin/EducationEditor';
import PublicationsEditor from './pages/admin/PublicationsEditor';
import PubPackagesEditor from './pages/admin/PubPackagesEditor';
import StarterTemplatesEditor from './pages/admin/StarterTemplatesEditor';
import MessagesView from './pages/admin/MessagesView';
import AnalyticsView from './pages/admin/AnalyticsView';

// Layout Components
import MainLayout from './components/layouts/MainLayout';
import AdminLayout from './components/layouts/AdminLayout';

// Analytics Tracker
import AnalyticsTracker from './components/AnalyticsTracker';
import ScrollToTop from './components/ScrollToTop';

// Context Providers
import { ProfileProvider } from './contexts/ProfileContext';
import { ExperienceProvider } from './contexts/ExperienceContext';
import { EducationProvider } from './contexts/EducationContext';
import { PublicationProvider } from './contexts/PublicationContext';
import { PubPackageProvider } from './contexts/PubPackageContext';
import { StarterTemplateProvider } from './contexts/StarterTemplateContext';
import { BlogProvider } from './contexts/BlogContext';
import { CourseProvider } from './contexts/CourseContext';
import NotFoundPage from './pages/public/NotFoundPage';
import CourseStudio from './pages/admin/CourseStudio';

function App() {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Protected Route Component
  const ProtectedRoute = ({ children }: { children: ReactElement }) => {
    if (loading) return <div className="flex h-screen items-center justify-center">Loading...</div>;
    if (!user) return <Navigate to="/admin/login" />;
    return children;
  };

  return (
    <ProfileProvider>
      <ExperienceProvider>
        <EducationProvider>
          <PublicationProvider>
            <PubPackageProvider>
              <StarterTemplateProvider>
                <BlogProvider>
                  <CourseProvider>
                  <Router>
              <ScrollToTop />
              <ToastContainer position="bottom-right" />
              <AnalyticsTracker />
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<MainLayout />}>
                  <Route index element={<HomePage />} />
                  <Route path="about" element={<Navigate to="/resume" replace />} />
                  <Route path="projects" element={<ProjectsPage />} />
                  <Route path="projects/:id" element={<ProjectDetailPage />} />
                  <Route path="resume" element={<ResumePage />} />
                  <Route path="contact" element={<ContactPage />} />
                  <Route path="publications" element={<PublicationsPage />} />
                  <Route path="open-source" element={<OpenSourcePage />} />
                  <Route path="pub-packages" element={<PubPackagesPage />} />
                  <Route path="starter-templates" element={<StarterTemplatesPage />} />
                  <Route path="writing" element={<WritingPage />} />
                  <Route path="writing/series/:courseSlug" element={<CoursePage />} />
                  <Route path="writing/series/:courseSlug/:moduleSlug" element={<CourseLessonPage />} />
                  <Route path="writing/articles/:articleSlug" element={<ArticleDetailPage />} />
                  <Route path="*" element={<NotFoundPage />} />
                </Route>

                {/* Admin Routes */}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute>
                      <AdminLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<AdminDashboard />} />
                  <Route path="profile" element={<ProfileEditor />} />
                  <Route path="projects" element={<ProjectsEditor />} />
                  <Route path="categories" element={<CategoriesEditor />} />
                  <Route path="experience" element={<ExperienceEditor />} />
                  <Route path="education" element={<EducationEditor />} />
                  <Route path="publications" element={<PublicationsEditor />} />
                  <Route path="pub-packages" element={<PubPackagesEditor />} />
                  <Route path="starter-templates" element={<StarterTemplatesEditor />} />
                  <Route path="course" element={<CourseStudio />} />
                  <Route path="messages" element={<MessagesView />} />
                  <Route path="analytics" element={<AnalyticsView />} />
                </Route>
              </Routes>
                  </Router>
                  </CourseProvider>
                </BlogProvider>
              </StarterTemplateProvider>
            </PubPackageProvider>
          </PublicationProvider>
        </EducationProvider>
      </ExperienceProvider>
    </ProfileProvider>
  );
}

export default App;
